import React from 'react';
import cn from '../../lib/cn';
import Reveal from './Reveal';
import DotTrail from './DotTrail';
import { TextReveal } from './TextReveal';

/** Page section wrapper: owns the vertical rhythm and the scroll anchor. */
export function Section({ id, className, children, tone = 'canvas', ...rest }) {
  const tones = {
    canvas: 'bg-canvas text-ink-900',
    soft: 'bg-canvas-soft text-ink-900',
    ink: 'bg-ink-950 text-canvas',
    none: '',
  };

  return (
    <section
      id={id}
      className={cn('relative py-20 sm:py-28 lg:py-36', tones[tone], className)}
      {...rest}
    >
      {children}
    </section>
  );
}

/**
 * Eyebrow + title + optional lede, with the dot-trail ornament.
 * `align` switches between the left-aligned editorial default and centred.
 *
 * `title` may be a string, or an array of strings for a multi-line heading —
 * an array gets the line-mask reveal, with each line sliding up just after the
 * one above it. (An array is required for that effect: a mask has to wrap each
 * line separately, which a `<br />` inside one element cannot express.)
 */
export function SectionHeading({
  eyebrow,
  title,
  lede,
  align = 'left',
  tone = 'dark',
  className,
  action,
}) {
  const centred = align === 'center';
  const titleTone = tone === 'light' ? 'text-canvas' : 'text-ink-950';
  const ledeTone = tone === 'light' ? 'text-canvas/65' : 'text-ink-700/80';
  const eyebrowTone = tone === 'light' ? 'text-champagne-300' : 'text-champagne-500';

  return (
    <div
      className={cn(
        'flex flex-col gap-5',
        centred ? 'items-center text-center' : 'items-start',
        action && !centred && 'md:flex-row md:items-end md:justify-between md:gap-10',
        className
      )}
    >
      <div className={cn('flex flex-col gap-4', centred ? 'items-center' : 'items-start', 'max-w-2xl')}>
        {eyebrow && (
          <Reveal className={cn('flex items-center gap-3', centred && 'justify-center')}>
            <DotTrail count={4} tone={tone === 'light' ? 'gold' : 'ink'} />
            <span className={cn('eyebrow', eyebrowTone)}>{eyebrow}</span>
          </Reveal>
        )}

        <h2
          className={cn(
            'font-display text-[2.1rem] leading-[1.08] sm:text-5xl lg:text-[3.4rem]',
            titleTone
          )}
        >
          {Array.isArray(title) ? (
            <TextReveal lines={title} delay={60} step={120} />
          ) : (
            <TextReveal lines={[title]} delay={60} />
          )}
        </h2>

        {lede && (
          <Reveal delay={220} variant="up" distance={16}>
            <p className={cn('max-w-xl text-[0.98rem] leading-relaxed sm:text-base', ledeTone)}>
              {lede}
            </p>
          </Reveal>
        )}
      </div>

      {action && (
        <Reveal delay={300} variant="up" distance={16} className={cn(centred && 'mt-2')}>
          {action}
        </Reveal>
      )}
    </div>
  );
}

export default Section;
